import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { FormFlowPage, IllustrativeOptions } from "src/components";
import { createStyles } from "src/styles";

import { BoldText } from "src/components/v1";

import { enums } from "@fraction/shared";
import belowIcon from "src/images/illustrative-buttons/below-credit-icon.png";
import excellentIcon from "src/images/illustrative-buttons/excellent-credit-icon.png";
import goodIcon from "src/images/illustrative-buttons/good-credit-icon.png";
import unsureIcon from "src/images/illustrative-buttons/unsure-credit-icon.png";

const CREDIT_OPTIONS = [
  { value: enums.CreditScoreGuess.EXCELLENT, label: "Excellent (720+)", image: excellentIcon },
  { value: enums.CreditScoreGuess.GOOD, label: "Good (640-719)", image: goodIcon },
  { value: enums.CreditScoreGuess.BELOW, label: "Decent (639-)", image: belowIcon },
  { value: enums.CreditScoreGuess.UNSURE, label: "I don't know", image: unsureIcon },
];

export interface CreditScoreProps {
  onChange: (value: enums.CreditScoreGuess) => void;
  value?: enums.CreditScoreGuess;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const styles = createStyles({
  options: {
    marginBottom: "24px",
  },
});

const CreditScore = ({ onNext, onBack, style, focusRef, value, onChange, ...props }: CreditScoreProps) => {
  const validate = useCallback(() => !!value, [value]);
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const handleChangeChoice = useCallback(
    (choice: enums.CreditScoreGuess) => {
      onChange(choice);
    },
    [value, onChange]
  );

  return (
    <FormFlowPage
      {...props}
      title="How's your credit?"
      description={
        <>
          Fraction has a <BoldText>minimum credit score of 640</BoldText>. We will pull credit to verify, but,
          if you have an idea of what your score is, please let us know. If there are multiple applicants,
          please provide the highest score.
        </>
      }
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      <IllustrativeOptions
        label="Credit score"
        placeholder="If you know it, please provide an estimate of your credit"
        focusRef={focusRef}
        value={value}
        onChange={handleChangeChoice}
        options={CREDIT_OPTIONS}
        containerStyle={styles.options}
      />
    </FormFlowPage>
  );
};

export default memo(CreditScore);
